import PropTypes from "prop-types";
import { alpha, styled } from "@mui/material/styles";
import { Box } from "@mui/material";

const RootStyle = styled("span")(({ theme, ownerState }) => {
  const isLight = theme.palette.mode === "light";
  const { color = "default", variant = "ghost" } = ownerState;

  const styleFilled = (color) => ({
    color: theme.palette[color]?.contrastText || "#fff",
    backgroundColor: theme.palette[color]?.main || theme.palette.grey[500],
  });

  const styleOutlined = (color) => ({
    color: theme.palette[color]?.main || theme.palette.grey[800],
    backgroundColor: "transparent",
    border: `1px solid ${
      theme.palette[color]?.main || theme.palette.grey[500]
    }`,
  });

  const styleGhost = (color) => ({
    color: theme.palette[color]?.main || theme.palette.grey[700],
    backgroundColor: alpha(theme.palette[color]?.main || "#919EAB", 0.16),
  });

  return {
    height: 22,
    minWidth: 22,
    lineHeight: 0,
    borderRadius: 6,
    cursor: "default",
    alignItems: "center",
    whiteSpace: "nowrap",
    display: "inline-flex",
    justifyContent: "center",
    padding: theme.spacing(0, 1),
    fontSize: theme.typography.pxToRem(12),
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.grey[800],
    backgroundColor: theme.palette.grey[300],

    ...(color !== "default"
      ? {
          ...(variant === "filled" && styleFilled(color)),
          ...(variant === "outlined" && styleOutlined(color)),
          ...(variant === "ghost" && styleGhost(color)),
        }
      : {
          ...(variant === "outlined" && {
            backgroundColor: "transparent",
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.grey[500_32]}`,
          }),
          ...(variant === "ghost" && {
            color: isLight
              ? theme.palette.text.secondary
              : theme.palette.common.white,
            backgroundColor: theme.palette.grey[500_16],
          }),
        }),
  };
});

Label.propTypes = {
  children: PropTypes.node,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  color: PropTypes.string,
  variant: PropTypes.oneOf(["filled", "outlined", "ghost"]),
  sx: PropTypes.object,
};

Label.defaultProps = {
  color: "default",
  variant: "ghost",
};

export default function Label({
  children,
  color = "default",
  variant = "ghost",
  startIcon,
  endIcon,
  sx,
}) {
  const iconStyle = {
    width: 16,
    height: 16,
    "& svg, img": { width: 1, height: 1, objectFit: "cover" },
  };

  return (
    <RootStyle
      ownerState={{ color, variant }}
      sx={{
        ...(startIcon && { pl: 0.75 }),
        ...(endIcon && { pr: 0.75 }),
        ...sx,
      }}
    >
      {startIcon && <Box sx={{ mr: 0.75, ...iconStyle }}>{startIcon}</Box>}
      {children}
      {endIcon && <Box sx={{ ml: 0.75, ...iconStyle }}>{endIcon}</Box>}
    </RootStyle>
  );
}
